import * as React from 'react';
import { useEffect, useState } from 'react';

import { defaultWrapLinesPreference, mediumPageSizePreference, paginationLabels } from 'src/commons/tables';

import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Link,
  Pagination,
  Table,
} from '@amzn/awsui-components-react-v3';

import { useCollection } from '@amzn/awsui-collection-hooks';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';

import { MetricStorePageHeader } from 'src/components/metricstore/components';

import { UnauthorizedModal } from 'src/components/metricstore/components-V3';
import { EmptyState } from 'src/commons/EmptyState';
import {
  CUSTODIAN,
  GLADSTONE_GROUP,
  GLADSTONE_WORKSPACE,
  PENDING_CUSTODIAN_REVIEW,
  RUNNING,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { listReviews, listSeasons } from 'src/api/notifications';
import { ReviewDecisionModal } from './reviewDecisionModal';
import { createReviewHistoryLink } from 'src/routes';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import { getAgreementDifference } from 'src/components/reviews/utils';

export interface ReviewAgreementTableProps extends CommonPageProps {
  setAllAgreementResources: any;
  setDiffModalVisible: any;
  setAgreementDifferences: any;
  setAgreementValidationResult: any;
  selectedAgreementResources: any;
  agreementResourceMap: any;
  setAgreementResourceMap: any;
  setNotifications: any;
  userInfo: any;
  loadingReviews: any;
  setLoadingReviews: any;
  resourceColumnDefinitions: any;
}

export const ReviewAgreementTable = (props: ReviewAgreementTableProps) => {
  const [allItems, setItems] = useState([]);
  const [onboardModal, setOnboardModal] = useState(false);
  const [custodianAction, setCustodianAction] = useState(undefined);
  const [reviewDecisionModalVisible, setReviewDecisionModalVisible] = useState(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const agreementColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'agreementDataDiffDetail',
      header: 'Review response',
      cell: (item) => (
        <Link
          onClick={() => {
            props.setDiffModalVisible(true);
            props.setAgreementDifferences(item);
            const resources = props.agreementResourceMap?.get(JSON.stringify(item));
            props.setAgreementValidationResult(resources?.[0]?.agreementValidationResult);
          }}
        >
          View response diff
        </Link>
      ),
      minWidth: 300,
    },
    {
      id: 'submittedBy',
      header: 'Submitted by',
      cell: (item) => extractValues(props.agreementResourceMap?.get(JSON.stringify(item)), 'reviewedBy'),
      minWidth: 300,
    },
    {
      id: 'resourcesTypes',
      header: 'Resources types',
      cell: (item) => extractValues(props.agreementResourceMap?.get(JSON.stringify(item)), 'resourceType'),
      minWidth: 300,
    },
    {
      id: 'resourceNumber',
      header: 'Resource count',
      cell: (item) => props.agreementResourceMap?.get(JSON.stringify(item))?.length,
      minWidth: 300,
      sortingField: 'resourceNumber',
    },
  ];

  const extractValues = (reviews, property) => {
    const values = reviews?.filter((r) => r[property] !== undefined).map((r) => r[property]);
    return [...new Set(values)].join(', ');
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  const getReviews = async (request) => {
    let result = [];
    let nextToken = null;
    try {
      do {
        const res = await listReviews(request);
        result.push(...res.reviews);
        nextToken = res.nextToken;
        request.nextToken = nextToken;
      } while (nextToken != null);
    } catch (e) {
      console.log(e);
    }
    return result;
  };

  const listAllReviews = async () => {
    try {
      // Get seasons first
      const listSeasonsResponse = await listSeasons({ status: RUNNING });
      const seasonIdList = listSeasonsResponse.seasons.map((r) => r.seasonId);

      // Build all requests
      const requests = [];

      seasonIdList.forEach((seasonId) => {
        // Midway reviews
        requests.push(getReviews({ seasonId }));

        // Group reviews
        props.userInfo?.memberGroupIds?.forEach((groupId) => {
          requests.push(
            getReviews({
              seasonId,
              reviewerType: GLADSTONE_GROUP,
              reviewerId: groupId,
            }),
          );
        });

        // Workspace reviews
        props.userInfo?.memberWorkspaceIds?.forEach((workspaceId) => {
          requests.push(
            getReviews({
              seasonId,
              reviewerType: GLADSTONE_WORKSPACE,
              reviewerId: workspaceId,
            }),
          );
        });
      });

      // Execute all requests in parallel
      const results = await Promise.all(requests);
      return results.flat();
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      return [];
    }
  };

  const generateAgreementDataResourceMap = async () => {
    const agreementMap = new Map();
    let allReviews = await listAllReviews();
    let itemSet = new Set();
    for (let review of allReviews) {
      if (review.reviewerPersona == CUSTODIAN && review.reviewStatus == PENDING_CUSTODIAN_REVIEW) {
        const resourceAgreementsBefore = review.resourceAgreementsBefore;
        const resourceAgreements = review.latestConsumerReviewRecord?.resourceAgreements;
        if (resourceAgreements == null) {
          continue;
        }
        const diff = getAgreementDifference(resourceAgreements, resourceAgreementsBefore);

        const item = {
          seasonId: review.seasonId,
          resourceId: review.resourceId,
          namespace: review.namespace,
          resourceType: review.resourceType,
          reviewId: review.reviewId,
          overrideAgreementsReason: review.latestConsumerReviewRecord?.overrideAgreementsReason,
          agreementValidationResult: review.latestConsumerReviewRecord?.agreementValidationResult,
          reviewedAt: review.latestConsumerReviewRecord?.reviewedAt,
          reviewerType: review.latestConsumerReviewRecord?.reviewerType,
          reviewedBy: review.latestConsumerReviewRecord?.reviewedBy,
          reviewAction: review.latestConsumerReviewRecord?.action,
          reviewComment: review.latestConsumerReviewRecord?.comment,
          agreements: resourceAgreements,
          agreementDataDiff: diff,
        };
        if (itemSet.has(JSON.stringify(item))) {
          continue;
        }
        itemSet.add(JSON.stringify(item));

        const key = JSON.stringify(diff);

        if (agreementMap.has(key)) {
          let resources = agreementMap.get(key);
          resources.push(item);
          agreementMap.set(key, resources);
        } else {
          agreementMap.set(key, [item]);
        }
      }
    }
    return agreementMap;
  };

  const handleRefresh = async () => {
    props.setLoadingReviews(true);

    const agreementMap = await generateAgreementDataResourceMap();

    props.setAgreementResourceMap(agreementMap);
    let items = [];
    for (let key of agreementMap.keys()) {
      items.push(JSON.parse(key));
    }

    setItems(items);

    props.setLoadingReviews(false);
  };

  const { items, collectionProps, paginationProps } = useCollection(allItems, {
    filtering: {
      empty: <EmptyState title='No reviews' subtitle='No pending reviews.' />,
      noMatch: <EmptyState title='No matches' subtitle='We can’t find a match.' />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
  });

  useEffect(() => {
    const { selectedItems } = collectionProps;

    props.setAgreementDifferences(selectedItems);

    if (props.agreementResourceMap != null) {
      props.setAllAgreementResources(props.agreementResourceMap?.get(JSON.stringify(selectedItems[0])));
    }
  }, [collectionProps.selectedItems]);

  const handleButtonAction = async (e) => {
    setReviewDecisionModalVisible(true);
    setCustodianAction(e.detail.id);
  };

  const closeReviewDecisionModal = () => {
    setReviewDecisionModalVisible(false);
  };

  return (
    <>
      <>
        <Table
          {...collectionProps}
          loadingText='Loading reviews'
          loading={props.loadingReviews}
          columnDefinitions={agreementColumnDefinitions}
          items={items}
          wrapLines={preferences.wrapLines}
          resizableColumns={true}
          selectionType='single'
          header={
            <>
              <MetricStorePageHeader
                buttons={[
                  {
                    text: '',
                    icon: 'refresh',
                    onItemClick: handleRefresh,
                  },
                  {
                    text: 'History',
                    onItemClick: () => props.setRedirect(createReviewHistoryLink('custodian')),
                  },
                  {
                    text: 'Actions',
                    onItemClick: handleButtonAction,
                    items: [
                      {
                        text: 'Approve',
                        id: 'approve',
                        disabled:
                          props.selectedAgreementResources == null || props.selectedAgreementResources.length == 0,
                      },
                      {
                        text: 'Deny',
                        id: 'deny',
                        disabled:
                          props.selectedAgreementResources == null || props.selectedAgreementResources.length == 0,
                      },
                      {
                        text: 'Needs more data',
                        id: 'needsMoreData',
                        disabled:
                          props.selectedAgreementResources == null || props.selectedAgreementResources.length == 0,
                      },
                    ],
                  },
                ]}
                counter={`(${allItems.length})`}
                subheader={'Submissions pending review'}
              />
            </>
          }
          empty={<EmptyState title={'No pending reviews'} subtitle='No pending reviews' />}
          pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
          preferences={
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={mediumPageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          }
        />
      </>

      {custodianAction && reviewDecisionModalVisible && props.selectedAgreementResources && (
        <ReviewDecisionModal
          action={custodianAction}
          visible={reviewDecisionModalVisible}
          dismiss={closeReviewDecisionModal}
          resources={props.selectedAgreementResources}
          setNotifications={props.setNotifications}
          selectedResources={props.selectedAgreementResources}
          refresh={handleRefresh}
          resourceColumnDefinitions={props.resourceColumnDefinitions}
          {...props}
        />
      )}

      <UnauthorizedModal visible={onboardModal} onClose={() => setOnboardModal(false)} />
    </>
  );
};
